import BlockIcon from "@mui/icons-material/Block";
import DirectionsRunIcon from "@mui/icons-material/DirectionsRun";
import FreeBreakfastIcon from "@mui/icons-material/FreeBreakfast";
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import PunchClockIcon from "@mui/icons-material/PunchClock";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import SpaceDashboardIcon from "@mui/icons-material/SpaceDashboard";
import TuneIcon from "@mui/icons-material/Tune";
import { Stack } from "@mui/material";
import { type FC, type ReactElement, type ReactNode, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { SystemTabs } from "../../../components/UIToolkit/Clickable/TabsAndChips/SystemTabs/SystemTabs";
import type { SystemTabBadge } from "../../../components/UIToolkit/Clickable/TabsAndChips/SystemTabs/utils";
import { BusyOverlay } from "../../../components/UIToolkit/molecules/BusyOverlay";
import { SystemPageHeader } from "../../../components/UIToolkit/molecules/SystemPageHeader";
import SystemButton from "../../../components/UIToolkit/SystemButton/SystemButton";
import type { PbjModel } from "../../../utils/models";
import { usePbjAuditWorkedSource, usePbjBuildingSummaries, usePbjUpcomingTurnover } from "../hooks/usePbjAudit";
import { addBuildingSummaries, selectedResults } from "../network/networkMath";
import { usePbjNetwork } from "../network/PbjNetworkContext";
import { useScopeLabel } from "../network/useScopeLabel";
import { PbjDownloadMenu } from "../PbjDownloadMenu";
import { subTabsPillSx } from "../PredictionTab/colors";
import { formatPeriodRange } from "../pbjGridFormat";
import { ActualsFallbackWarning } from "./ActualsFallbackWarning";
import type { AuditSubTab, OpenAuditDestination } from "./auditSubTabs";
import { BreaksRemovedTable } from "./BreaksRemovedTable";
import { ExcludedFromPbjTable } from "./ExcludedFromPbjTable";
import { HoursByRoleChart } from "./HoursByRoleChart";
import { LongShiftsTable } from "./LongShiftsTable";
import { MissingPunchesTable } from "./MissingPunchesTable";
import { NetworkSummaryTab } from "./NetworkSummary/NetworkSummaryTab";
import { ShortShiftsTable } from "./ShortShiftsTable";
import { TerminatedTable } from "./TerminatedTable";
import { UpcomingTurnoverTable } from "./UpcomingTurnoverTable";

type Props = {
	committedStart: string;
	committedEnd: string;
	savedModel: PbjModel;
	// True while a page-level recalculation (upsert) is running; dims + freezes the sub-tables.
	isCalculating?: boolean;
	// Wraps a "Change period" header button in the page-level period/facility popover.
	renderChangePeriod: (trigger: ReactElement) => ReactNode;
	/** Opens Submission Sheets — where the Summary sends hours that have no PBJ job. */
	onOpenSubmission: () => void;
};

// Product-spec default for the Turnover tab's view-only "within N days" filter.
const DEFAULT_TURNOVER_THRESHOLD_DAYS = 60;

/** Count pill for a sub-tab. `undefined` (still loading / no range) hides the badge; 0 is shown
 * explicitly — a clean audit ("0 long shifts") is a meaningful result, not absence of data. */
const countBadge = (count: number | undefined): SystemTabBadge | undefined =>
	count === undefined ? undefined : { content: count > 999 ? "999+" : count, color: "primary" };

export const AuditTab: FC<Props> = ({ committedStart, committedEnd, savedModel, isCalculating = false, renderChangePeriod, onOpenSubmission }) => {
	const { t } = useTranslation();
	const { selectedBuildingIds, setSelectedBuildingIds } = usePbjNetwork();
	// Audit opens on the Summary — for one building or a whole network.
	const [subTab, setSubTab] = useState<AuditSubTab>("summary");
	// Lifted here (not persisted) so the Turnover tab badge and table share one query per threshold.
	const [turnoverThresholdDays, setTurnoverThresholdDays] = useState(DEFAULT_TURNOVER_THRESHOLD_DAYS);

	const periodRange = formatPeriodRange(committedStart, committedEnd);
	const scopeLabel = useScopeLabel();
	const subtitle = [scopeLabel, periodRange, t("pbj_model.audit.subtitle_suffix")].filter(Boolean).join(" · ");

	// The tab badges come off each building's Summary row: its counts are the lengths of these same lists,
	// so opening Audit costs one request per building instead of every list for every building. Turnover is
	// as-of-today and threshold-driven, so it is read from its own list.
	const upcomingTurnover = usePbjUpcomingTurnover(turnoverThresholdDays);
	const summaries = usePbjBuildingSummaries(committedStart, committedEnd);
	const counts = useMemo(
		() => summaries.data && addBuildingSummaries(selectedResults(summaries.data, selectedBuildingIds).map((result) => result.data)),
		[summaries.data, selectedBuildingIds],
	);
	const selectedTurnover = upcomingTurnover.data?.filter((row) => selectedBuildingIds.size === 0 || selectedBuildingIds.has(row.buildingId));

	// A Summary figure opens its list with the Building filter on that one building (or the current selection).
	const openDestination = useCallback<OpenAuditDestination>(
		(destination, buildingId) => {
			if (buildingId) setSelectedBuildingIds(new Set([buildingId]));
			if (destination === "submission") onOpenSubmission();
			else setSubTab(destination);
		},
		[setSelectedBuildingIds, onOpenSubmission],
	);
	// DAT-810: a department with no PBJ mirror reads the Audit numbers off the staffing schedule
	// instead. Resolved once per department (no date range) and disclosed in a banner above the sub-tab
	// bar, so it is visible whichever sub-tab is open.
	const workedSource = usePbjAuditWorkedSource();

	const subTabs = useMemo(
		() => [
			{ label: t("pbj_model.network.summary.title"), value: "summary" as const, icon: <SpaceDashboardIcon sx={{ fontSize: 18 }} /> },
			{
				label: t("pbj_model.audit.tabs.long_shifts"),
				value: "long_shifts" as const,
				icon: <HourglassBottomIcon sx={{ fontSize: 18 }} />,
				badge: countBadge(counts?.longShiftCount),
			},
			{
				label: t("pbj_model.audit.tabs.short_shifts"),
				value: "short_shifts" as const,
				icon: <HourglassTopIcon sx={{ fontSize: 18 }} />,
				badge: countBadge(counts?.shortShiftCount),
			},
			{
				label: t("pbj_model.audit.tabs.missing_punches"),
				value: "missing_punches" as const,
				icon: <PunchClockIcon sx={{ fontSize: 18 }} />,
				badge: countBadge(counts?.missingPunchCount),
			},
			{
				label: t("pbj_model.audit.tabs.breaks_removed"),
				value: "breaks_removed" as const,
				icon: <FreeBreakfastIcon sx={{ fontSize: 18 }} />,
				badge: countBadge(counts?.breaksRemovedCount),
			},
			{
				label: t("pbj_model.audit.tabs.terminated"),
				value: "terminated" as const,
				icon: <PersonOffIcon sx={{ fontSize: 18 }} />,
				badge: countBadge(counts?.terminatedCount),
			},
			{
				label: t("pbj_model.audit.tabs.excluded"),
				value: "excluded" as const,
				icon: <BlockIcon sx={{ fontSize: 18 }} />,
				badge: countBadge(counts?.excludedCount),
			},
			{
				label: t("pbj_model.audit.tabs.upcoming_turnover"),
				value: "upcoming_turnover" as const,
				icon: <DirectionsRunIcon sx={{ fontSize: 18 }} />,
				badge: countBadge(selectedTurnover?.length),
			},
			{
				label: t("pbj_model.audit.tabs.hours_by_role"),
				value: "hours_by_role" as const,
				icon: <ShowChartIcon sx={{ fontSize: 18 }} />,
				// No count badge — this sub-tab is a chart, not a countable row list.
			},
		],
		[t, counts, selectedTurnover?.length],
	);

	return (
		<Stack spacing={2.5} data-testid="pbj-model-audit-tab">
			<SystemPageHeader
				dataTestId="pbj-model-audit-header"
				eyebrow={t("pbj_model.tabs.audit")}
				title={t("pbj_model.audit.title")}
				subtitle={subtitle}
				actions={
					<>
						{renderChangePeriod(
							<SystemButton buttonVariant="secondary" size="small" leadingIcon={<TuneIcon sx={{ fontSize: 16 }} />} noFullWidthMobile>
								{t("pbj_model.hero.change_period")}
							</SystemButton>,
						)}
						<PbjDownloadMenu committedStart={committedStart} committedEnd={committedEnd} />
					</>
				}
			/>
			{/* Above the tab bar on purpose: the disclosure applies to every sub-tab, including Excluded
			    from PBJ, which has nothing to fall back to (the banner copy says so).
			    The condition reads `hasPbjMirror`, the bool the API exposes precisely so the FE never has
			    to compare enum strings. An ERROR renders the `unknown` variant rather than nothing: each
			    of the seven data routes resolves its source independently server-side, so a failure of
			    THIS query alone would otherwise leave schedule-derived, gross, unmapped hours on screen
			    presented as the CMS basis — the wrong failure direction on a compliance surface. */}
			{workedSource.isError ? (
				<ActualsFallbackWarning variant="unknown" />
			) : (
				// In the network view one building without a mirror is enough: its rows are on screen too.
				workedSource.data?.some(({ data }) => !data.hasPbjMirror) && <ActualsFallbackWarning variant="fallback" />
			)}
			{/* No wrapper card — the bar sits on the canvas and each sub-tab renders its own section card(s). */}
			<SystemTabs<AuditSubTab>
				dataTestId="pbj-model-audit-subtabs"
				tabs={subTabs}
				currentTab={subTab}
				onTabChange={setSubTab}
				sx={(theme) => subTabsPillSx(theme, { onCanvas: true })}
			/>
			<BusyOverlay busy={isCalculating} dataTestId="pbj-model-audit-recalculating">
				{subTab === "summary" && <NetworkSummaryTab summaries={summaries} committedEnd={committedEnd} onOpen={openDestination} />}
				{subTab === "long_shifts" && <LongShiftsTable startDate={committedStart} endDate={committedEnd} savedModel={savedModel} />}
				{subTab === "short_shifts" && <ShortShiftsTable startDate={committedStart} endDate={committedEnd} />}
				{subTab === "missing_punches" && <MissingPunchesTable startDate={committedStart} endDate={committedEnd} />}
				{subTab === "breaks_removed" && <BreaksRemovedTable startDate={committedStart} endDate={committedEnd} />}
				{subTab === "terminated" && <TerminatedTable startDate={committedStart} endDate={committedEnd} />}
				{subTab === "excluded" && <ExcludedFromPbjTable startDate={committedStart} endDate={committedEnd} />}
				{subTab === "upcoming_turnover" && <UpcomingTurnoverTable thresholdDays={turnoverThresholdDays} onThresholdDaysChange={setTurnoverThresholdDays} />}
				{subTab === "hours_by_role" && <HoursByRoleChart startDate={committedStart} endDate={committedEnd} />}
			</BusyOverlay>
		</Stack>
	);
};
